import { useCallback, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

export type AnchoredSide = 'top' | 'bottom';
export type AnchoredPlacement =
  'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top-end' | 'top';

interface Options {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  floatingRef: RefObject<HTMLElement | null>;
  placement?: AnchoredPlacement;
  /** Gap between the anchor and the floating element, in px. */
  offset?: number;
  /** Match the floating element's width to the anchor's. */
  matchWidth?: boolean;
  /** Minimum distance from the viewport edge, in px. */
  padding?: number;
}

export interface AnchoredResult {
  top: number;
  left: number;
  /** Set when `matchWidth` is on. */
  width?: number | undefined;
  side: AnchoredSide;
  /** False until the first measurement lands (keep the element hidden). */
  ready: boolean;
}

const DEFAULT_OFFSET = 6;
const DEFAULT_PADDING = 8;
const HALF = 2;

/**
 * Viewport-aware anchored positioning (portal-friendly, dependency-free): places a
 * floating element next to an anchor, flipping to the opposite side when it would
 * overflow and clamping along the cross axis. Recomputes on scroll/resize while open.
 */
export function useAnchoredPosition({
  open,
  anchorRef,
  floatingRef,
  placement = 'bottom-start',
  offset = DEFAULT_OFFSET,
  matchWidth = false,
  padding = DEFAULT_PADDING,
}: Options): AnchoredResult {
  const [position, setPosition] = useState<AnchoredResult>({
    top: 0,
    left: 0,
    side: 'bottom',
    ready: false,
  });

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) return;

    const a = anchor.getBoundingClientRect();
    const fw = floating.offsetWidth;
    const fh = floating.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const [sideReq, alignReq = 'start'] = placement.split('-') as [AnchoredSide, string?];

    const spaceBelow = vh - a.bottom - offset - padding;
    const spaceAbove = a.top - offset - padding;
    let side = sideReq;
    if (side === 'bottom' && fh > spaceBelow && spaceAbove > spaceBelow) side = 'top';
    else if (side === 'top' && fh > spaceAbove && spaceBelow > spaceAbove) side = 'bottom';

    const top = side === 'bottom' ? a.bottom + offset : a.top - offset - fh;
    const width = matchWidth ? a.width : undefined;
    // `matchWidth` asks for the anchor's width, but CSS can refuse it: the panel carries a
    // `min-width`, so a narrow anchor — a 36px kebab — yields a panel three times wider. Clamping
    // against the requested width then let it hang off the viewport. `offsetWidth` is what the
    // element actually occupies, whether or not a width was applied on a previous pass, so the
    // larger of the two is the width to keep on screen.
    const w = Math.max(width ?? fw, fw);

    let left: number;
    if (alignReq === 'end') left = a.right - w;
    else if (alignReq === 'center') left = a.left + a.width / HALF - w / HALF;
    else left = a.left;
    left = Math.min(Math.max(left, padding), vw - w - padding);

    // Returning `prev` unchanged when nothing moved is not only a spared render: the
    // `ResizeObserver` below re-enters `update` on every size change, and a `setPosition` that
    // always allocates would keep handing React a new object for an identical position. That is
    // how a resize-observe loop starts.
    setPosition((prev) =>
      prev.ready &&
      prev.top === top &&
      prev.left === left &&
      prev.width === width &&
      prev.side === side
        ? prev
        : { top, left, width, side, ready: true },
    );
  }, [anchorRef, floatingRef, placement, offset, matchWidth, padding]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition((prev) => (prev.ready ? { ...prev, ready: false } : prev));
      return undefined;
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    /**
     * Re-measure when either element changes size (HS-1284).
     *
     * `update` reads `floating.offsetHeight` to decide whether the panel fits below the anchor and,
     * when it flips above, where its top edge lands. Measuring once on open assumes the panel is
     * already its final size — which is false whenever its content settles afterwards. A `Select`
     * whose options come from a request opens short, grows when the response lands, and keeps the
     * coordinates of the panel that was measured rather than the one on screen. That is the
     * institution field on every creation form: wrong on the first open, right on the second, once
     * the query cache has made the list synchronous.
     *
     * Observing the anchor too covers the mirror case — a trigger that reflows under the panel.
     */
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            update();
          });
    if (observer) {
      if (floatingRef.current) observer.observe(floatingRef.current);
      if (anchorRef.current) observer.observe(anchorRef.current);
    }

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, [open, update, anchorRef, floatingRef]);

  return position;
}
